/**
 * Task and your-note schemas. PRD-003 DATA-03, DATA-04, DATA-05, DATA-08.
 *
 * Tasks carry the 16-value type enum and 11-value status enum (10 documented
 * lifecycle states plus the 'split' terminal state used by parents after
 * PR-39 split).
 */

import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common.js';
import {
  coachingDepthSchema,
  intakeModeSchema,
  taskStatusSchema,
  taskTypeSchema,
} from './enums.js';

export const taskSchema = z.object({
  id: uuidSchema,
  subscriber_id: uuidSchema,
  type: taskTypeSchema,
  status: taskStatusSchema,
  brief: z.string().min(1),
  intake_mode: intakeModeSchema.nullable(),
  coaching_depth: coachingDepthSchema.nullable(),
  parent_task_id: uuidSchema.nullable(),
  order_in_backlog: z.number().int().nonnegative().nullable(),
  pull_time: isoTimestampSchema.nullable(),
  due_at: isoTimestampSchema.nullable(),
  delivered_at: isoTimestampSchema.nullable(),
  approved_at: isoTimestampSchema.nullable(),
  created_at: isoTimestampSchema,
  updated_at: isoTimestampSchema,
  deleted_at: isoTimestampSchema.nullable(),
});
export type Task = z.infer<typeof taskSchema>;

/**
 * Allowed forward status transitions per PRD-003 DATA-04 and ARCH-006 §5.
 *
 * Subscribers create tasks in intake_shape_check and transition them to
 * backlog. Operators handle every transition from ready_to_pull through
 * delivered. Subscribers transition delivered to approved. Agents may only
 * set status='agent_draft' (AI-06, AI-13).
 */
export const taskStatusTransitionsForwardOnly: Record<
  z.infer<typeof taskStatusSchema>,
  ReadonlyArray<z.infer<typeof taskStatusSchema>>
> = {
  intake_shape_check: ['backlog', 'split'],
  backlog: ['ready_to_pull', 'split'],
  ready_to_pull: ['pulled'],
  pulled: ['in_progress', 'agent_draft'],
  in_progress: ['agent_draft', 'eric_reviewing', 'ready_to_deliver'],
  agent_draft: ['eric_reviewing'],
  eric_reviewing: ['in_progress', 'ready_to_deliver'],
  ready_to_deliver: ['delivered'],
  delivered: ['approved'],
  approved: [],
  split: [],
};

/**
 * Task intake payload: what the subscriber submits before shape-check writes
 * the canonical task row. PRD-003 PR-11.
 */
export const taskIntakeSchema = z.object({
  brief: z.string().min(1),
  type: taskTypeSchema,
  intake_mode: intakeModeSchema,
  coaching_depth: coachingDepthSchema.optional(),
});
export type TaskIntake = z.infer<typeof taskIntakeSchema>;

/**
 * Shape-check verdict from the assistant. PRD-003 PR-12 + AI-11.
 */
export const shapeCheckVerdictSchema = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('tightened'),
    tightened_brief: z.string().min(1),
    flagged_risks: z.array(z.string()).default([]),
  }),
  z.object({
    outcome: z.literal('oversized'),
    proposed_children: z
      .array(
        z.object({
          brief: z.string().min(1),
          type: taskTypeSchema,
        }),
      )
      .min(2)
      .max(4),
    why_oversized: z.string().min(1),
  }),
]);
export type ShapeCheckVerdict = z.infer<typeof shapeCheckVerdictSchema>;

/**
 * "Your notes" entry. PRD-003 DATA-08 + PR-23.
 *
 * Append-only invariant in V1: subscribers can insert but never update or
 * delete. Promotion into a context-doc sub-field is recorded by setting
 * `promoted_to_context_at` (operator-driven via T-068 promote_your_note).
 */
export const yourNoteSchema = z.object({
  id: uuidSchema,
  subscriber_id: uuidSchema,
  body: z.string().min(1),
  promoted_to_context_at: isoTimestampSchema.nullable(),
  promoted_section: z.string().nullable(),
  created_at: isoTimestampSchema,
  deleted_at: isoTimestampSchema.nullable(),
});
export type YourNote = z.infer<typeof yourNoteSchema>;

/**
 * Write shape for inserting a your-notes row. Append-only: only body is set
 * by the subscriber; everything else is derived server-side.
 */
export const yourNoteInsertSchema = z.object({
  body: z.string().min(1),
});
export type YourNoteInsert = z.infer<typeof yourNoteInsertSchema>;
